import { TokenMapper } from "src/containers/authentication/mappings/token";
import { inject, injectable } from "tsyringe";
import { Observable, of } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { GenericSuccessResponse } from "src/infra/http/types";
import { Token, TokenSSO } from "src/containers/authentication/models/token";
import { User } from "src/shared/models/user.model";
import { AuthenticationRepository } from "src/containers/authentication/repositories/authentication/authentication-repository";
import { SessionService } from "src/containers/authentication/services/session/session-service";
import { autoBind } from "src/shared/decorators/autobind";
import { DI } from "src/infra/di/container";

@injectable()
export class AuthenticationService {
    static key = "AuthenticationService";

    constructor(
        @inject("AuthenticationRepository")
        private repo: AuthenticationRepository,
        @inject("SessionService") private session: SessionService
    ) {}

    @autoBind
    getToken(authCode: string, isSSO = false): Observable<Token> {
        if (isSSO) {
            return this.getTokenSSO(authCode);
        }
        return this.getTokenConventional(authCode);
    }

    @autoBind
    getTokenConventional(authCode: string): Observable<Token> {
        return this.repo
            .getTokenConventional(authCode)
            .pipe(map((json) => Token.fromJson<Token>(json)));
    }

    @autoBind
    getTokenSSO(authCode: string): Observable<Token> {
        return this.repo
            .getTokenSSO(authCode)
            .pipe(map((json) => TokenSSO.fromJson<TokenSSO>(json)))
            .pipe(map(TokenMapper.fromTokenSSO));
    }

    @autoBind
    refreshToken(): Observable<Token> {
        return this.repo
            .refreshToken(this.session.getAuthorization())
            .pipe(map((json) => Token.fromJson<Token>(json)));
    }

    @autoBind
    me(token: Token): Observable<User> {
        return this.repo
            .me(token, token.applicationUser)
            .pipe(map((data) => User.fromJson<User>(data)));
    }

    @autoBind
    logout(isSSO = false): Observable<GenericSuccessResponse> {
        if (isSSO) {
            return this.logoutSSO();
        }
        return this.logoutConventional();
    }

    @autoBind
    logoutConventional(): Observable<GenericSuccessResponse> {
        return this.repo.logout(this.session.getAuthorization()).pipe(
            tap(() => {
                this.session.postLogout(true);
            })
        );
    }

    @autoBind
    logoutSSO(): Observable<GenericSuccessResponse> {
        return this.repo.logoutSSO(this.session.getToken()?.value || "").pipe(
            tap(() => {
                this.session.postLogout(true);
            }),
            catchError(() => {
                this.session.postLogout(true);
                return of({
                    code: 500,
                    status: "Failed to logout",
                } as GenericSuccessResponse);
            })
        );
    }
}

DI.put(AuthenticationService.key, AuthenticationService);
